import { useState } from 'react';
import { type UseUpdateHookResult } from '@saviynt/biz-entity-catalog';

import { updateClient } from '../data-sources';
import { type Client, type ClientFormSubmitPayload } from '../types';

/**
 * BizEntityCatalog update hook adapter for client credentials.
 */
export function useClientUpdate(): UseUpdateHookResult<Client> {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = async (
    id: string | number,
    data: Partial<Client>
  ): Promise<Client> => {
    setIsLoading(true);
    setError(null);

    try {
      // Form submissions pass extra fields (publicCert, keySource, jwksUrl) via the
      // Partial<Client> generic. Cast once to access form-specific properties.
      const formData = data as Partial<Client & ClientFormSubmitPayload>;

      const updated = await updateClient(String(id), {
        clientName: formData.clientName,
        description: formData.description,
        roles: formData.roles,
        customClaims: formData.customClaims,
        enabled: formData.enabled,
        publicCert: formData.publicCert ?? undefined,
        jwksUrl: formData.jwksUrl,
      });

      return updated;
    } catch (err) {
      const typedError = err instanceof Error ? err : new Error(String(err));

      setError(typedError);
      throw typedError;
    } finally {
      setIsLoading(false);
    }
  };

  return { mutate, isLoading, error };
}
